/**
 * Cart drawer selling-plan selector.
 *
 * Rendered per line item in the sidebar cart drawer (see
 * snippets/cart-item-selling-plan.liquid). Switches a line item between a
 * one-time purchase and an Appstle / native selling plan (or between plans)
 * using the native Cart AJAX API:
 *
 *   POST /cart/change.js  { line, quantity, id: <line_item_key>, selling_plan: <selling_plan_id> }
 *   (a `selling_plan` of '' — empty string — switches back to one-time purchase)
 *
 *   A 302 redirect (HTML) response is treated as success: Shopify sends it
 *   when the change is applied but alters the line item (variant/key).
 *
 * After a successful change the drawer is re-rendered through the
 * `cart-drawer:refresh` event handled in assets/cart-drawer.js.
 */
class CartSellingPlan extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);

    // Read data attributes here, not in the constructor. When the element is
    // created from HTML (including re-renders via innerHTML), the constructor
    // runs before the data-* attributes are parsed, so this.dataset would be
    // empty there. connectedCallback runs after attributes are set.
    this.line = this.dataset.line;
    this.quantity = this.dataset.quantity;
    this.lineKey = this.dataset.lineKey;

    this.addEventListener("click", this.handleClick);
    this.addEventListener("change", this.handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("change", this.handleChange);
  }

  handleClick(event) {
    // Ignore interactions that start inside a <select>
    if (event.target.closest("select")) return;

    const option = event.target.closest(".wt-cart-selling-plan__option");
    if (!option || !this.contains(option)) return;

    const value = option.dataset.value;

    if (value === "") {
      // One-time purchase. Skip only when it's genuinely already one-time;
      // if the line is on another app's plan (data-on-other-plan), the card
      // is shown as selected but clicking it must still switch back.
      const isSelected = option.classList.contains(
        "wt-cart-selling-plan__option--selected",
      );
      if (isSelected && this.dataset.onOtherPlan !== "true") {
        return;
      }
      this.switchPlan("");
      return;
    }

    // Subscription group card
    const select = option.querySelector(".wt-cart-selling-plan__select");
    if (!select) return;

    if (option.classList.contains("wt-cart-selling-plan__option--selected")) {
      // Already on this group -> just open the plan dropdown
      select.focus();
      return;
    }

    // Switching to subscription: use the currently shown plan.
    // (Also covers groups with a single plan, where no change event fires.)
    this.switchPlan(select.value);
  }

  handleChange(event) {
    const select = event.target.closest(".wt-cart-selling-plan__select");
    if (!select) return;

    this.switchPlan(select.value);
  }

  switchPlan(sellingPlanId) {
    this.setLoading(true);

    // Identify the line by its unique line item key (variant id + properties
    // hash) rather than a bare variant id: Appstle subscription lines carry
    // custom properties, and their key is `variantId:hash`. Sending a bare
    // variant id for such a line makes Shopify treat the change as a
    // line-structure change.
    //
    // `selling_plan` is always sent: a plan id applies the plan, while an
    // empty string removes it (one-time purchase). Omitting the field would
    // leave the current plan untouched.
    const body = JSON.stringify({
      line: this.line,
      quantity: this.quantity,
      id: this.lineKey,
      selling_plan: sellingPlanId || "",
    });

    fetch(`${window.routes.cart_change_url}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    })
      .then((response) => {
        const contentType = response.headers.get("Content-Type") || "";

        // Shopify's /cart/change.js can answer a selling-plan change with a
        // 302 redirect to /cart (HTML) when the change is applied but alters
        // the line item's variant/key, signalling "reload the page". Treat a
        // non-JSON response as success and refresh the drawer below.
        if (!contentType.includes("application/json")) {
          return { ok: true };
        }

        return response.json().then((state) => {
          if (state.errors) {
            throw new Error(
              Array.isArray(state.errors)
                ? state.errors.join(", ")
                : state.errors,
            );
          }
          return state;
        });
      })
      .then(() => {
        document.dispatchEvent(new Event("cart-drawer:refresh"));
      })
      .catch((error) => {
        console.error("Error switching selling plan:", error);
        this.setLoading(false);
      });
  }

  setLoading(loading) {
    this.classList.toggle("is-loading", loading);
    this.setAttribute("aria-busy", loading ? "true" : "false");
  }
}

if (!customElements.get("cart-selling-plan")) {
  customElements.define("cart-selling-plan", CartSellingPlan);
}
